import React, { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Container,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField
} from '@mui/material';
import AutoSearch from 'components/common/AutoSearch';
import CheckboxList from 'components/common/CheckboxList';
import { CompanyType } from 'models/CompanyModel';
import { VoyageStatus } from 'models/VoyageModel';
import { ExportMethod, ExportType, PriceTerms, StockStatus, VehicleType } from 'models/StockModel';
import useParties from 'modules/parties/hooks/useParties';
import companyList from 'recoil/atoms/companyList';
import voyageFamily from 'recoil/atoms/voyageFamily';
import spinner from 'recoil/atoms/spinner';
import loadingYardList from 'recoil/selectors/loadingYardList';
import Api from 'utils/api';
import { forceDownload, replaceItemAtIndex } from 'utils/common';


type Filter = {
    status: string,
    price_terms?: string,
    company_id?: number,
    party_id?: number,
    voyage_id?: number,
    yard_id: number[],
    export_method: string[],
    export_type: string[],
    vehicle_type: string[],
    documents?: number,
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const exportMethods = Object.values(ExportMethod);
const exportTypes = Object.values(ExportType);
const vehicleTypes = Object.values(VehicleType);
const carStatuses = Object.values(StockStatus);
const priceTerms = Object.values(PriceTerms);

const columnNames = [
    'company.name',
    'chassis_number',
    'make',
    'model',
    'unit_price',
    'price_terms',
    'export_method',
    'export_type',
    'vehicle_type',
    'color',
    'year',
    'fuel',
    'engine_model',
    'engine_number',
    'engine_size',
    'seats',
    'cargo_capacity',
    'net_weight',
    'gross_weight',
    'length',
    'width',
    'height',

    'yard.port.name',
    'yard.name',
    'yard.date_in',
    'yard.key',
    'yard.location',

    'voyage.number',
    'voyage.pol.name',
    'voyage.pod.name',
    'voyage.etd',
    'voyage.eta',

    'consignee.country',
    'consignee.city',
    'consignee.address',
    'consignee.phone',
    'consignee.email',
    'consignee.company_or_name',
    'consignee.tin',

    'notify.country',
    'notify.city',
    'notify.address',
    'notify.phone',
    'notify.email',
    'notify.company_or_name',
    'notify.tin',

    'dealer.country',
    'dealer.city',
    'dealer.address',
    'dealer.phone',
    'dealer.email',
    'dealer.company_or_name',
    'dealer.tin',
];

const Inner = () => {
    const {t} = useTranslation('cars');
    const companies = useRecoilValue(companyList(CompanyType.Shipper));
    const voyagesPending = useRecoilValue(voyageFamily(VoyageStatus.Pending));
    const voyagesLoaded = useRecoilValue(voyageFamily(VoyageStatus.Loaded));
    const voyagesDeparted = useRecoilValue(voyageFamily(VoyageStatus.Departed));
    const yards = useRecoilValue(loadingYardList);
    const setSpinner = useSetRecoilState(spinner);
    const {findParty} = useParties();

    const [filters, setFilters] = useState<Filter>({
        status: StockStatus.Pending,
        yard_id: [],
        export_method: [],
        export_type: [],
        vehicle_type: [],
    });
    const [sorts, setSorts] = useState([
        {
            col: 'company_id',
            dir: 'asc',
            checked: true,
        },
        {
            col: 'id',
            dir: 'asc',
            checked: false,
        },
        {
            col: 'price_terms',
            dir: 'asc',
            checked: false,
        },
        {
            col: 'yard_id',
            dir: 'asc',
            checked: false,
        },
        {
            col: 'export_method',
            dir: 'asc',
            checked: false,
        },
        {
            col: 'export_type',
            dir: 'asc',
            checked: false,
        },
        {
            col: 'vehicle_type',
            dir: 'asc',
            checked: false,
        },
    ]);
    const [checked, setChecked] = useState(columnNames.map(() =>  false));

    const voyages = useMemo(() => {
        const total = [
            ...voyagesPending,
            ...voyagesLoaded,
            ...voyagesDeparted,
        ];

        if (!filters.yard_id.length)
            return total;

        const ports = yards.filter(y => filters.yard_id.includes(y.id)).map(y => y.port.id);

        return total.filter(v => ports.includes(v.pol.id))
    }, [voyagesPending, voyagesLoaded, voyagesDeparted, yards, filters.yard_id]);

    const companyOptions = [
        {value: '', label: t('any')},
        ...companies.map(c => ({value: c.id, label: c.cleanName}))
    ];

    const voyageOptions = [
        {value: '', label: t('any')},
        {value: 0, label: t('no_voyage')},
        ...voyages.map(c => ({value: c.id, label: c.number})),
    ];

    const sortChanged = (e: React.ChangeEvent<HTMLInputElement>) => setSorts(old => {
        const idx = old.findIndex(s => s.col === e.target.name);
        return replaceItemAtIndex(old, idx, {...old[idx], checked: e.target.checked})
    });

    const download = () => {
        setSpinner(true);
        Api.cars.downloadList({
            filters,
            sorts: Object.values(sorts).filter(s => s.checked),
            columns: columnNames.filter((_, i) => checked[i]),
        })
            .then(forceDownload)
            .then(() => setSpinner(false));
    };

    return (
        <Container maxWidth="xl">
            <Grid container columnSpacing={2}>
                <Grid item xs={12} sm={3}>
                    <h4>{t('saved_lists')}</h4>
                </Grid>

                <Grid item xs={12} sm={3}>
                    <h4>{t('filters')}</h4>

                    <TextField
                        select
                        id="status"
                        label={t('status')}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={filters.status}
                        onChange={e => setFilters(old => ({
                            ...old,
                            status: e.target.value
                        }))}
                    >
                        {carStatuses.map(value => (
                            <MenuItem key={value} value={value}>
                                {value}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        id="shipper"
                        label={t('shipper')}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={filters.company_id || ''}
                        onChange={e => setFilters(old => ({
                            ...old,
                            company_id: e.target.value === '' ? undefined : Number(e.target.value)
                        }))}
                    >
                        {companyOptions.map(({value, label}) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <AutoSearch
                        search={s => findParty(s)}
                        onChange={r => setFilters(old => ({
                            ...old,
                            party_id: r ? r.id : undefined,
                        }))}
                        label={t('parties')}
                        keepValue
                    />

                    <FormControl
                        fullWidth
                        margin="normal"
                        size="small"
                    >
                        <InputLabel id="yards-label">{t('yards')}</InputLabel>
                        <Select
                            labelId="yards-label"
                            id="yards-select"
                            multiple
                            value={filters.yard_id}
                            onChange={e => setFilters(old => {
                                const {value} = e.target;

                                return {
                                    ...old,
                                    yard_id: (typeof value === 'string' ? value.split(',') : value) as number[]
                                }
                            })}
                            input={<OutlinedInput label={t('yards')}/>}
                            renderValue={selected => selected.map(id => {
                                const yard = yards.find(y => y.id === id)!;
                                return `${yard.port.name} ${yard.name}`;
                            }).join(', ')}
                            MenuProps={MenuProps}
                        >
                            {yards.map(yard => (
                                <MenuItem key={yard.id} value={yard.id}>
                                    <Checkbox checked={filters.yard_id.indexOf(yard.id) > -1}/>
                                    <ListItemText primary={`${yard.port.name} ${yard.name}`}/>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        select
                        id="voyage"
                        label={t('voyage')}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={filters.voyage_id || ''}
                        onChange={e => setFilters(old => ({
                            ...old,
                            voyage_id: e.target.value === '' ? undefined : Number(e.target.value)
                        }))}
                    >
                        {voyageOptions.map(({value, label}) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <MultiSelect
                        id="export-methods"
                        label={t('export_method')}
                        value={filters.export_method}
                        onChange={e => setFilters(old => {
                            const {value} = e.target;

                            return {
                                ...old,
                                export_method: typeof value === 'string' ? value.split(',') : value
                            }
                        })}
                        options={exportMethods}
                    />

                    <MultiSelect
                        id="export-types"
                        label={t('export_type')}
                        value={filters.export_type}
                        onChange={e => setFilters(old => {
                            const {value} = e.target;

                            return {
                                ...old,
                                export_type: typeof value === 'string' ? value.split(',') : value
                            }
                        })}
                        options={exportTypes}
                    />

                    <MultiSelect
                        id="vehicle-types"
                        label={t('vehicle_type')}
                        value={filters.vehicle_type}
                        onChange={e => setFilters(old => {
                            const {value} = e.target;

                            return {
                                ...old,
                                vehicle_type: typeof value === 'string' ? value.split(',') : value
                            }
                        })}
                        options={vehicleTypes}
                    />

                    <TextField
                        select
                        id="priceTerms"
                        label={t('price_terms')}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={filters.price_terms || ''}
                        onChange={e => setFilters(old => ({
                            ...old,
                            price_terms: e.target.value || undefined
                        }))}
                    >
                        <MenuItem value="">{t('any')}</MenuItem>
                        {priceTerms.map(value => (
                            <MenuItem key={value} value={value}>
                                {value}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        id="documents"
                        label={t('documents')}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={filters.documents === undefined ? '' : filters.documents}
                        onChange={e => setFilters(old => ({
                            ...old,
                            documents: e.target.value === '' ? undefined : Number(e.target.value)
                        }))}
                    >
                        <MenuItem value="">{t('any')}</MenuItem>
                        <MenuItem value="1">{t('with')}</MenuItem>
                        <MenuItem value="0">{t('without')}</MenuItem>
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={3}>
                    <h4>{t('sort_by')}</h4>

                    <FormGroup>
                        {sorts.map(s => (
                            <FormControlLabel
                                key={s.col}
                                control={<Checkbox
                                    name={s.col}
                                    checked={s.checked}
                                    onChange={sortChanged}
                                />}
                                label={s.col.replace(/[._]/g, ' ')}
                            />
                        ))}
                    </FormGroup>
                </Grid>

                <Grid item xs={12} sm={3}>
                    <Card>
                        <CardHeader
                            title={t('show_columns')}
                        />
                        <CardContent>
                            <Box sx={{overflow: 'auto', height: 'calc(100vh - 200px)'}}>
                                <CheckboxList
                                    items={columnNames}
                                    formatter={col => col.replace(/[._]/g, ' ')}
                                    checked={checked}
                                    setChecked={setChecked}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box textAlign="right" pt={2}>
                <Button onClick={download}>{t('download')}</Button>
            </Box>
        </Container>
    );
};


type MultiSelectProps = {
    id: string,
    label: string,
    value: string[],
    onChange: (e: { target: { value: string[] | string, name: string } }) => void,
    options: string[],
}

const MultiSelect = (e: MultiSelectProps) => {
    return (
        <FormControl
            fullWidth
            margin="normal"
            size="small"
        >
            <InputLabel id={`${e.id}-label`}>{e.label}</InputLabel>
            <Select
                labelId={`${e.id}-label`}
                id={`${e.id}-select`}
                multiple
                value={e.value}
                onChange={e.onChange}
                input={<OutlinedInput label={e.label}/>}
                renderValue={selected => selected.join(', ')}
                MenuProps={MenuProps}
            >
                {e.options.map(opt => (
                    <MenuItem key={opt} value={opt}>
                        <Checkbox checked={e.value.indexOf(opt) > -1}/>
                        <ListItemText primary={opt}/>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
};

const CarLists = () => {
    return (
        <Suspense fallback={null}>
            <Inner/>
        </Suspense>
    )
}


export default CarLists;
